import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useEffect, useState } from 'react';
import { fetchLoginEvents } from '../api/loginEvents';
import './LoginEventsPage.css';

const DAY_OPTIONS = [1, 7, 30];
const LIMIT_OPTIONS = [50, 100, 200, 500];
const RESULT_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'SUCCESS', label: '성공' },
  { value: 'FAIL', label: '실패' },
];

function LoginEventsPage() {
  const { t } = useLanguage();
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState('');
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadLoginEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetchLoginEvents({
        days,
        limit,
        result,
        search: searchTerm,
      });
      setEvents(Array.isArray(response.data) ? response.data : []);
      setTotal(Number(response.total ?? 0));
      setLastLoadedAt(new Date());
    } catch (err) {
      console.error('failed to fetch login events:', err);
      setError('로그인 이벤트를 불러오지 못했습니다.');
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [days, limit, result, searchTerm]);

  useEffect(() => {
    loadLoginEvents();
  }, [loadLoginEvents]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const nextSearchTerm = searchInput.trim();
    if (nextSearchTerm === searchTerm) {
      loadLoginEvents();
      return;
    }

    setSearchTerm(nextSearchTerm);
  };

  const handleSearchReset = () => {
    setSearchInput('');

    if (!searchTerm) {
      loadLoginEvents();
      return;
    }

    setSearchTerm('');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';

    const normalizedDateString = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString)
      ? dateString
      : `${dateString.replace(' ', 'T')}Z`;
    const date = new Date(normalizedDateString);
    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  };

  const formatLastLoadedAt = () => {
    if (!lastLoadedAt) return '-';

    return `${String(lastLoadedAt.getHours()).padStart(2, '0')}:${String(lastLoadedAt.getMinutes()).padStart(2, '0')}:${String(lastLoadedAt.getSeconds()).padStart(2, '0')}`;
  };

  const getResultLabel = (value) => {
    if (value === 'SUCCESS') return '성공';
    if (value === 'FAIL') return '실패';
    return value || '-';
  };

  return (
    <div className="login-events-page">
      <div className="page-header">
        <div className="page-title-group">
          <div>
            <h2 className="page-title">{t("로그인 이벤트")}</h2>
            <p className="page-subtitle">{t("최근 사용자 로그인 성공/실패 기록을 확인합니다.")}</p>
          </div>
          <div className="login-events-summary-bar">
            <span>{t("조회 결과")}</span>
            <strong>{total.toLocaleString()}</strong>
          </div>
        </div>

        <div className="header-actions">
          <form className="login-event-search-form" onSubmit={handleSearchSubmit}>
            <div className="login-event-search-field">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="login-event-search-icon"
              >
                <path
                  d="M10.5 18a7.5 7.5 0 1 1 5.3-12.8A7.5 7.5 0 0 1 10.5 18Zm0-2a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Zm6.3.4 4.4 4.4-1.4 1.4-4.4-4.4 1.4-1.4Z"
                  fill="currentColor"
                />
              </svg>
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t("사용자명 또는 이메일")}
                disabled={loading}
                aria-label={t("사용자명 또는 이메일 검색")}
              />
            </div>
            <button className="btn-search" type="submit" disabled={loading}>
              <svg aria-hidden="true" viewBox="0 0 24 24" className="btn-search-icon">
                <path
                  d="M10.5 18a7.5 7.5 0 1 1 5.3-12.8A7.5 7.5 0 0 1 10.5 18Zm0-2a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Zm6.3.4 4.4 4.4-1.4 1.4-4.4-4.4 1.4-1.4Z"
                  fill="currentColor"
                />
              </svg>
              {t("검색")}
            </button>
            {(searchInput || searchTerm) && (
              <button
                className="btn-clear"
                type="button"
                onClick={handleSearchReset}
                disabled={loading}
              >
                {t("초기화")}
              </button>
            )}
          </form>

          <div className="filter-group" aria-label={t("조회 기간")}>
            {DAY_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`filter-chip ${days === option ? 'active' : ''}`}
                onClick={() => setDays(option)}
                disabled={loading}
              >
                {option}일
              </button>
            ))}
          </div>

          <select
            className="filter-select"
            value={result}
            onChange={(event) => setResult(event.target.value)}
            disabled={loading}
            aria-label={t("로그인 결과")}
          >
            {RESULT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
            disabled={loading}
            aria-label={t("조회 개수")}
          >
            {LIMIT_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}개</option>
            ))}
          </select>

          <button className="btn-refresh" type="button" onClick={loadLoginEvents} disabled={loading}>
            {t("새로고침")}
          </button>
        </div>
      </div>

      <div className="login-events-meta">
        <span>조회 기간: 최근 {days}일</span>
        {searchTerm && <span>검색어: {searchTerm}</span>}
        <span>마지막 갱신: {formatLastLoadedAt()}</span>
      </div>

      <div className="table-card">
        <table className="login-events-table">
          <thead>
            <tr>
              <th>{t("일시")}</th>
              <th>{t("결과")}</th>
              <th>{t("사용자")}</th>
              <th>{t("이메일")}</th>
              <th>IP</th>
              <th>{t("역할")}</th>
              <th>{t("사유")}</th>
              <th>User-Agent</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="table-empty">{t("로그인 이벤트를 불러오는 중입니다...")}</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="8" className="table-error">{error}</td>
              </tr>
            ) : events.length > 0 ? (
              events.map((event, index) => (
                <tr key={`${event.timestamp || event.username || 'login-event'}-${index}`}>
                  <td className="date-text">{formatDateTime(event.timestamp)}</td>
                  <td>
                    <span className={`result-badge ${event.result === 'SUCCESS' ? 'success' : 'fail'}`}>
                      {getResultLabel(event.result)}
                    </span>
                  </td>
                  <td className="username-text">{event.username || '-'}</td>
                  <td className="detail-text">{event.email || '-'}</td>
                  <td className="mono-text">{event.ip || '-'}</td>
                  <td className="detail-text">{event.role || '-'}</td>
                  <td className="reason-text">{event.reason || '-'}</td>
                  <td className="agent-text" title={event.userAgent || '-'}>
                    {event.userAgent || '-'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="table-empty">{t("조회된 로그인 이벤트가 없습니다.")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LoginEventsPage;
